import React, { useEffect, useRef, useState } from "react";

/**
 * Counts a numeric stat up when it scrolls into view ("9+", "67+", "144K+").
 * The final value renders immediately for reduced-motion users, and the
 * animation always lands on the exact string passed in.
 */
export default function CountUp({ value, duration = 1400 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const match = String(value).match(/^(\d+(?:\.\d+)?)(.*)$/);
    if (!match) return undefined;

    const target = parseFloat(match[1]);
    const suffix = match[2];
    const decimals = match[1].includes(".") ? match[1].split(".")[1].length : 0;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    let raf = 0;
    const run = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        if (p < 1) {
          setDisplay((target * eased).toFixed(decimals) + suffix);
          raf = window.requestAnimationFrame(tick);
        } else {
          setDisplay(value);
        }
      };
      raf = window.requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.disconnect();
            run();
          }
        });
      },
      { threshold: 0.5 }
    );

    io.observe(el);
    return () => {
      io.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
}
